import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CategoryIcon from "@mui/icons-material/Category";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    error: { main: "#DC2626" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', sans-serif",
    h2: { fontFamily: "'DM Serif Display', serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

const fadeUp = keyframes`from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}`;
const cardEntrance = keyframes`from{opacity:0;transform:translateY(12px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}`;
const skeletonPulse = keyframes`0%,100%{opacity:1}50%{opacity:.4}`;

const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg,#EEF2FF 0%,#F0F2F8 40%,#E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora',sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle,rgba(37,99,235,.06) 0%,transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 720, margin: "0 auto" });

const SectionCard = styled(Paper, { shouldForwardProp: (p) => p !== "delay" })(
  ({ theme, delay }) => ({
    borderRadius: 16,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: "0 1px 3px rgba(0,0,0,.05),0 8px 24px rgba(0,0,0,.04)",
    padding: "24px 28px",
    marginBottom: 20,
    animation: `${fadeUp} .5s ease both`,
    animationDelay: `${delay ?? 0}ms`,
    transition: "box-shadow .2s ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(37,99,235,.06),0 12px 32px rgba(0,0,0,.06)",
    },
  })
);

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all .2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const PrimaryBtn = styled(Button)(() => ({
  background: "linear-gradient(135deg,#2563EB 0%,#1E40AF 100%)",
  color: "#fff",
  borderRadius: 10,
  padding: "10px 24px",
  fontSize: 13,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora',sans-serif",
  boxShadow: "0 4px 14px rgba(37,99,235,.28)",
  transition: "all .2s ease",
  "&:hover": {
    background: "linear-gradient(135deg,#1D4ED8 0%,#1E3A8A 100%)",
    boxShadow: "0 6px 20px rgba(37,99,235,.38)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const StyledInput = styled("input")(() => ({
  width: "100%",
  fontFamily: "'Sora',sans-serif",
  fontSize: 14,
  color: "#0F1117",
  background: alpha("#F0F2F8", 0.8),
  border: `1px solid ${alpha("#000", 0.1)}`,
  borderRadius: 10,
  padding: "11px 14px 11px 38px",
  outline: "none",
  boxSizing: "border-box",
  transition: "all .2s ease",
  "&:focus": {
    borderColor: "#2563EB",
    background: "#fff",
    boxShadow: "0 0 0 3px rgba(37,99,235,.08)",
  },
  "&::placeholder": { color: "#9CA3AF" },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: ".6px",
  textTransform: "uppercase",
  color: "#9CA3AF",
  marginBottom: 6,
  fontFamily: "'Sora',sans-serif",
}));

const CategoryRow = styled(Paper, {
  shouldForwardProp: (p) => p !== "itemdelay",
})(({ theme, itemdelay }) => ({
  borderRadius: 12,
  border: `1px solid ${alpha("#000", 0.07)}`,
  boxShadow: "0 1px 4px rgba(0,0,0,.04),0 4px 14px rgba(0,0,0,.03)",
  padding: "14px 18px",
  display: "flex",
  alignItems: "center",
  gap: 14,
  marginBottom: 10,
  background: "#fff",
  animation: `${cardEntrance} .4s ease both`,
  animationDelay: `${itemdelay ?? 0}ms`,
  transition: "all .22s ease",
  "&:last-child": { marginBottom: 0 },
  "&:hover": {
    boxShadow: "0 4px 16px rgba(37,99,235,.08),0 8px 24px rgba(0,0,0,.05)",
    transform: "translateY(-1px)",
    borderColor: alpha("#2563EB", 0.18),
  },
}));

const CatIconBox = styled(Box)(({ theme }) => ({
  width: 38,
  height: 38,
  borderRadius: 10,
  flexShrink: 0,
  background: alpha(theme.palette.primary.main, 0.1),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const ActionBtn = styled(Button, {
  shouldForwardProp: (p) => p !== "btncolor",
})(({ theme, btncolor }) => {
  const map = {
    edit: { bg: alpha("#C4922A", 0.1), color: "#C4922A", hbg: "#C4922A" },
    delete: { bg: alpha("#DC2626", 0.08), color: "#DC2626", hbg: "#DC2626" },
  };
  const s = map[btncolor] || map.edit;
  return {
    minWidth: "unset",
    padding: "5px 12px",
    borderRadius: 7,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "none",
    background: s.bg,
    color: s.color,
    fontFamily: "'Sora',sans-serif",
    transition: "all .18s ease",
    "&:hover": {
      background: s.hbg,
      color: "#fff",
      transform: "translateY(-1px)",
      boxShadow: `0 4px 10px ${alpha(s.hbg, 0.3)}`,
    },
  };
});

const SkeletonRow = styled(Box)(() => ({
  borderRadius: 12,
  border: `1px solid ${alpha("#000", 0.06)}`,
  padding: "14px 18px",
  display: "flex",
  alignItems: "center",
  gap: 14,
  background: "#fff",
  marginBottom: 10,
  animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
}));

const Sb = ({ w = "100%", h = 14, r = 6 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: r,
      background: alpha("#000", 0.07),
      flexShrink: 0,
    }}
  />
);

const AdminNewsCategoryListPage = () => {
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState("");
  const [fetching, setFetching] = useState(true);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const fetchCategories = useCallback(() => {
    setFetching(true);
    axios
      .get(`${API_URL}/admin/newscategory/`)
      .then((res) => setCategories(res.data))
      .catch((err) => console.error("Error fetching categories:", err))
      .finally(() => setFetching(false));
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const createCategory = () => {
    if (!name.trim()) return;
    setCreating(true);
    axios
      .post(`${API_URL}/admin/newscategory/`, { name })
      .then((res) => {
        setCategories([...categories, res.data]);
        setName("");
      })
      .catch((err) => console.error("Error creating category:", err))
      .finally(() => setCreating(false));
  };

  const deleteCategory = (id) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      axios
        .delete(`${API_URL}/admin/newscategory/${id}/`)
        .then(() => setCategories(categories.filter((c) => c.id !== id)))
        .catch((err) => console.error("Error deleting category:", err));
    }
  };

  const editCategory = (id) => navigate(`/admin-edit-news-category/${id}`);

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />
      <PageWrapper>
        <ContentBox>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              animation: `${fadeUp} .4s ease both`,
              flexWrap: "wrap",
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 27 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                News Categories
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora',sans-serif",
                }}
              >
                Manage article categories
              </Typography>
            </Box>
            {!fetching && (
              <Chip
                label={`${categories.length} categor${
                  categories.length !== 1 ? "ies" : "y"
                }`}
                size="small"
                sx={{
                  ml: "auto",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  fontSize: 11,
                  background: alpha("#2563EB", 0.1),
                  color: "#2563EB",
                  border: `1px solid ${alpha("#2563EB", 0.2)}`,
                }}
              />
            )}
          </Box>

          {/* Create Card */}
          <SectionCard delay={50}>
            <CardLabel>Add New Category</CardLabel>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <FieldLabel>Category Name *</FieldLabel>
                <Box sx={{ position: "relative" }}>
                  <CategoryIcon
                    sx={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: 15,
                      color: "text.disabled",
                      pointerEvents: "none",
                    }}
                  />
                  <StyledInput
                    placeholder="e.g. Technology, Sports…"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && createCategory()}
                  />
                </Box>
              </Box>
              <PrimaryBtn
                startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
                disabled={creating || !name.trim()}
                onClick={createCategory}
                sx={{ mb: "1px" }}
              >
                {creating ? "Creating…" : "Create"}
              </PrimaryBtn>
            </Box>
          </SectionCard>

          {/* List Card */}
          <SectionCard delay={100}>
            <CardLabel>All Categories</CardLabel>

            {fetching ? (
              [...Array(4)].map((_, i) => (
                <SkeletonRow key={i} sx={{ animationDelay: `${i * 80}ms` }}>
                  <Sb w={38} h={38} r={10} />
                  <Box sx={{ flex: 1 }}>
                    <Sb w="40%" h={14} />
                  </Box>
                  <Sb w={55} h={28} r={7} />
                  <Sb w={62} h={28} r={7} />
                </SkeletonRow>
              ))
            ) : categories.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: 3,
                    background: alpha("#2563EB", 0.07),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <CategoryIcon
                    sx={{ fontSize: 24, color: "primary.main", opacity: 0.45 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "text.disabled",
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 600,
                  }}
                >
                  No categories yet
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.disabled",
                    fontFamily: "'Sora',sans-serif",
                    mt: 0.4,
                  }}
                >
                  Use the form above to create your first category
                </Typography>
              </Box>
            ) : (
              categories.map((cat, i) => (
                <CategoryRow key={cat.id} itemdelay={i * 55}>
                  <CatIconBox>
                    <CategoryIcon
                      sx={{ fontSize: 18, color: "primary.main" }}
                    />
                  </CatIconBox>
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: 14,
                      fontWeight: 700,
                      color: "text.primary",
                      fontFamily: "'DM Serif Display',serif",
                    }}
                  >
                    {cat.name}
                  </Typography>
                  <Stack direction="row" spacing={0.8}>
                    <Tooltip title="Edit category" arrow>
                      <ActionBtn
                        btncolor="edit"
                        onClick={() => editCategory(cat.id)}
                      >
                        <EditIcon sx={{ fontSize: 13, mr: 0.4 }} />
                        Edit
                      </ActionBtn>
                    </Tooltip>
                    <Tooltip title="Delete category" arrow>
                      <ActionBtn
                        btncolor="delete"
                        onClick={() => deleteCategory(cat.id)}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 13, mr: 0.4 }} />
                        Delete
                      </ActionBtn>
                    </Tooltip>
                  </Stack>
                </CategoryRow>
              ))
            )}
          </SectionCard>
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
};

export default AdminNewsCategoryListPage;
